/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx } from '@emotion/react'

import { EuiBadge, EuiFlexGroup, EuiFlexItem, EuiIcon } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import {
  getPrettyElasticsearchProjectSubtype,
  getPrettyObservabilityProjectSubtype,
  getPrettyProjectType,
  getProjectTypeIcon,
} from '@modules/project-lib/display'
import type { AnyProject } from '@modules/project-api/types'

const ProjectTypeIconAndLabel = ({ project }: { project: AnyProject }) => (
  <EuiFlexGroup gutterSize='s' responsive={false} alignItems='baseline'>
    <EuiFlexItem grow={false}>
      <EuiIcon
        type={getProjectTypeIcon(project.type)}
        // to center the icon against the text:
        // - given the difference in box-heights between icon (16px) and text (20px with line-height)
        // - while maintaining baseline alignment in the overall flex group for consistency with any text line this may be consumed in
        style={{ position: 'relative', top: 3 }}
      />
    </EuiFlexItem>
    <EuiFlexItem grow={false}>
      <FormattedMessage {...getPrettyProjectType(project.type)} />
    </EuiFlexItem>

    {project.type === 'elasticsearch' && (
      <EuiFlexItem grow={false} css={{ maxWidth: '60%' }}>
        <EuiBadge>
          <FormattedMessage {...getPrettyElasticsearchProjectSubtype(project.optimized_for)} />
        </EuiBadge>
      </EuiFlexItem>
    )}

    {project.type === 'observability' && (
      <EuiFlexItem grow={false} css={{ maxWidth: '60%' }}>
        <EuiBadge>
          <FormattedMessage {...getPrettyObservabilityProjectSubtype(project.product_tier)} />
        </EuiBadge>
      </EuiFlexItem>
    )}
  </EuiFlexGroup>
)

export default ProjectTypeIconAndLabel
